import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { useRef } from "react";
import { instruments } from "@/data/instruments";

function InstrumentScene({ instrument, index, total, progress }) {
  const N = total;
  // Each instrument gets a slot of 1/N in scroll progress
  const slot = 1 / N;
  const start = index * slot;
  const mid = start + slot * 0.5;
  const end = start + slot;

  // Entry / exit ranges
  const enterEnd = start + slot * 0.25;
  const exitStart = end - slot * 0.25;

  // Image transforms
  const scale = useTransform(
    progress,
    [start, enterEnd, exitStart, end],
    [0.55, 1.05, 1.05, 0.55]
  );
  const rotate = useTransform(
    progress,
    [start, end],
    [index % 2 === 0 ? -35 : 35, index % 2 === 0 ? 45 : -45]
  );
  const opacity = useTransform(
    progress,
    [start, enterEnd, exitStart, end],
    [0, 1, 1, 0]
  );
  const y = useTransform(
    progress,
    [start, enterEnd, exitStart, end],
    ["8%", "0%", "0%", "-8%"]
  );

  // Text transforms
  const textOpacity = useTransform(
    progress,
    [start, start + slot * 0.3, exitStart - slot * 0.05, exitStart],
    [0, 1, 1, 0]
  );
  const textX = useTransform(
    progress,
    [start, enterEnd, exitStart, end],
    ["-30px", "0px", "0px", "30px"]
  );

  const detailsOpacity = useTransform(
    progress,
    [start + slot * 0.4, start + slot * 0.55, exitStart - slot * 0.05, exitStart],
    [0, 1, 1, 0]
  );

  const numberScale = useTransform(
    progress,
    [start, mid, end],
    [0.8, 1, 0.8]
  );

  return (
    <>
      {/* Giant background index number */}
      <motion.div
        style={{ opacity, scale: numberScale }}
        className="absolute inset-0 flex items-center justify-center pointer-events-none"
        aria-hidden="true"
      >
        <span
          className="font-serif italic text-[55vh] leading-none font-light select-none"
          style={{ color: instrument.accent, opacity: 0.07 }}
        >
          {String(index + 1).padStart(2, "0")}
        </span>
      </motion.div>

      {/* Instrument image, centered in an organic frame */}
      <motion.div
        style={{ scale, rotate, opacity, y }}
        className="absolute inset-0 flex items-center justify-center pointer-events-none"
        data-testid={`scroll-instrument-${instrument.id}`}
      >
        <div
          className="relative w-[62vmin] h-[62vmin] rounded-full overflow-hidden flex items-center justify-center"
          style={{
            background: `radial-gradient(circle at 35% 30%, #F2ECE0 0%, #E8E1D5 55%, #D6CDBC 100%)`,
            boxShadow: `0 60px 100px -20px rgba(74,51,32,0.25), inset 0 0 60px rgba(74,51,32,0.04)`,
          }}
        >
          <img
            src={instrument.image}
            alt={instrument.name}
            className="w-[115%] h-[115%] object-cover"
            draggable="false"
            style={{ mixBlendMode: "multiply" }}
          />
          {/* Vignette overlay */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `radial-gradient(circle, transparent 55%, rgba(214,205,188,0.6) 100%)`,
            }}
          />
        </div>
      </motion.div>

      {/* Left side — label + name (glass card) */}
      <motion.div
        style={{ opacity: textOpacity, x: textX }}
        className="absolute left-4 md:left-8 lg:left-12 top-[15%] md:top-[18%] max-w-[320px] md:max-w-[380px] z-10"
      >
        <div
          className="relative pl-5 md:pl-6 py-5 pr-5 rounded-r-2xl rounded-l-sm"
          style={{
            background: "rgba(247, 245, 240, 0.72)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            boxShadow: "0 20px 40px -20px rgba(74,51,32,0.18), 0 0 0 1px rgba(26,26,24,0.04)",
          }}
        >
          {/* Accent bar */}
          <div
            className="absolute left-0 top-4 bottom-4 w-[3px] rounded-full"
            style={{ backgroundColor: instrument.accent }}
          />
          <div className="flex items-center gap-3 mb-4">
            <span
              className="text-[10px] uppercase tracking-[0.3em] font-medium"
              style={{ color: instrument.accent }}
            >
              {instrument.label}
            </span>
          </div>
          <h3 className="font-serif text-3xl md:text-4xl lg:text-5xl font-light text-ink leading-[0.95]">
            {instrument.name}
          </h3>
          <p className="mt-3 text-xs uppercase tracking-[0.18em] text-ink/55">
            {instrument.subtitle}
          </p>
        </div>
      </motion.div>

      {/* Right side — details (glass card) */}
      <motion.div
        style={{ opacity: detailsOpacity }}
        className="absolute right-4 md:right-8 lg:right-12 bottom-[15%] md:bottom-[18%] max-w-[320px] md:max-w-[380px] z-10 pointer-events-auto"
      >
        <div
          className="relative pl-5 md:pl-6 py-6 pr-5 rounded-l-2xl rounded-r-sm"
          style={{
            background: "rgba(247, 245, 240, 0.72)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            boxShadow: "0 20px 40px -20px rgba(74,51,32,0.18), 0 0 0 1px rgba(26,26,24,0.04)",
          }}
        >
          {/* Accent bar right side */}
          <div
            className="absolute right-0 top-4 bottom-4 w-[3px] rounded-full"
            style={{ backgroundColor: instrument.accent }}
          />
          <p className="text-[15px] md:text-base text-ink/80 leading-relaxed font-normal mb-6">
            {instrument.description}
          </p>
          <div className="grid grid-cols-2 gap-x-5 gap-y-4 mb-6">
            {instrument.details.map((d) => (
              <div key={d.k} className="border-t border-ink/15 pt-2.5">
                <div className="text-[9px] uppercase tracking-[0.2em] text-ink/50 font-medium">
                  {d.k}
                </div>
                <div className="text-sm text-ink mt-0.5 font-medium">{d.v}</div>
              </div>
            ))}
          </div>
          <div className="flex items-baseline justify-between gap-3 mb-5 pt-3 border-t border-ink/10">
            <div className="flex items-baseline gap-2">
              {instrument.priceOld && (
                <span className="text-xs text-ink/40 line-through">
                  {instrument.priceOld}
                </span>
              )}
              <span
                className="font-serif text-2xl font-medium"
                style={{ color: instrument.accent }}
              >
                {instrument.price}
              </span>
            </div>
          </div>
          <a
            href={instrument.link}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-cream text-[11px] uppercase tracking-[0.15em] font-medium hover:scale-105 transition-transform shadow-lg"
            style={{
              backgroundColor: instrument.accent,
              boxShadow: `0 10px 25px -5px ${instrument.accent}60`,
            }}
            data-testid={`scroll-cta-${instrument.id}`}
          >
            Voir le produit →
          </a>
        </div>
      </motion.div>
    </>
  );
}

export default function ScrollShowcase() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });

  const smooth = useSpring(scrollYProgress, {
    stiffness: 120,
    damping: 28,
    mass: 0.5,
  });

  // Section label that scrolls with progress
  const progressWidth = useTransform(smooth, [0, 1], ["0%", "100%"]);

  return (
    <section
      id="showcase"
      ref={ref}
      className="relative bg-cream"
      style={{ height: `${instruments.length * 100}vh` }}
      data-testid="scroll-showcase"
    >
      <div className="sticky top-0 h-screen w-full overflow-hidden">
        {/* Subtle ambient blobs */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 -left-40 w-[500px] h-[500px] rounded-full bg-sage/10 blur-[120px]" />
          <div className="absolute bottom-0 -right-40 w-[500px] h-[500px] rounded-full bg-terracotta/10 blur-[120px]" />
        </div>

        {/* Top bar */}
        <div className="absolute top-6 md:top-10 left-6 md:left-12 right-6 md:right-12 flex items-center justify-between z-20">
          <div className="flex items-center gap-3">
            <div className="w-1.5 h-1.5 rounded-full bg-terracotta pulse-ring" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-ink/60">
              La collection · Défilez pour explorer
            </span>
          </div>
          <span className="text-[10px] uppercase tracking-[0.3em] text-ink/60">
            Compiègne — Oise
          </span>
        </div>

        {/* Scenes */}
        {instruments.map((inst, i) => (
          <InstrumentScene
            key={inst.id}
            instrument={inst}
            index={i}
            total={instruments.length}
            progress={smooth}
          />
        ))}

        {/* Bottom progress bar */}
        <div className="absolute bottom-0 left-0 right-0 px-6 md:px-12 pb-6 z-20">
          <div className="h-[2px] bg-ink/10 relative overflow-hidden rounded-full">
            <motion.div
              style={{ width: progressWidth }}
              className="absolute left-0 top-0 h-full bg-terracotta"
            />
          </div>
          <div className="flex justify-between mt-3 text-[10px] uppercase tracking-[0.25em] text-ink/40">
            {instruments.map((inst, i) => (
              <span key={inst.id} className="hidden md:inline">
                {String(i + 1).padStart(2, "0")} · {inst.name.split(" ")[0]}
              </span>
            ))}
            <span className="md:hidden">
              Instruments 01 — 0{instruments.length}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
